import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { getData } from "../../../webService/webservice.js";
import { useStore } from "../../../Context/context.jsx";
import { getDateRange } from "../../../utils/get-DateRange.js";
import { exportDataToExcel } from "../../../utils/excel-Export.js";
import "../../../styles/Auth/purchase-report.css";

const PurchaseReport = () => {
    const today = new Date().toISOString().split("T")[0];
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [dateRange, setDateRange] = useState("Today");

    const { selectedStoreId } = useStore();
    const sessionToken = localStorage.getItem("Token");
    const role = localStorage.getItem("role");
    const adminStoreIds = JSON.parse(localStorage.getItem("storeIds") || "[]");

    // --- State Management ---
    const [activeTab, setActiveTab] = useState("overall");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({
        totalPOs: 0,
        totalPurchaseValue: 0,
        totalValueReceived: 0,
        totalOutstanding: 0,
        avgOrderValue: 0
    });

    // View Modal
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewPurchaseData, setViewPurchaseData] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Filters
    const [filters, setFilters] = useState({
        supplierId: "",
        invoiceNo: "",
        storeId: "",
        categoryId: "",
        productId: ""
    });



    // Dropdown Lists
    const [supplierList, setSupplierList] = useState([]);
    const [storeList, setStoreList] = useState([]);
    const [categoryList, setCategoryList] = useState([]);
    const [productList, setProductList] = useState([]);

    const [tableSearch, setTableSearch] = useState("");

    // --- Fetching Data ---

    // --- Helper for Safe Array Extraction ---
    const safeExtractArray = (res) => {
        if (!res || !res.data) return [];

        // Some return [{ paginatedResults: [...] }]
        if (Array.isArray(res.data) && res.data[0]?.paginatedResults) {
            return res.data[0].paginatedResults;
        }

        // Some APIs return directly in data
        if (Array.isArray(res.data)) return res.data;

        // Some return { results: [...] } or { results: [{ paginatedResults: [...] }] }
        if (res.data.results) {
            if (Array.isArray(res.data.results)) {
                if (res.data.results[0]?.paginatedResults) {
                    return res.data.results[0].paginatedResults;
                }
                return res.data.results;
            }
        }

        // Fallback for objects that might just be the data itself
        if (typeof res.data === 'object' && !Array.isArray(res.data)) {
            // Try to find any array inside
            for (let key in res.data) {
                if (Array.isArray(res.data[key])) return res.data[key];
            }
        }

        return [];
    };

    const fetchDropdownData = async (targetStoreId) => {
        try {
            const commonParams = { limit: 10000 };
            if (targetStoreId) commonParams.storeId = targetStoreId;

            const [suppRes, storeRes, catRes, prodRes] = await Promise.all([
                getData("suppliers/suppliers", commonParams, sessionToken),
                getData("stores/stores", { limit: 10000 }, sessionToken),
                getData("categories/categories", commonParams, sessionToken),
                getData("products/products", commonParams, sessionToken)
            ]);

            setSupplierList(safeExtractArray(suppRes));
            setStoreList(safeExtractArray(storeRes));
            setCategoryList(safeExtractArray(catRes));
            setProductList(safeExtractArray(prodRes));

        } catch (error) {
            console.error("Error fetching dropdown data:", error);
        }
    };

    const [debouncedFilters, setDebouncedFilters] = useState(filters);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedFilters(filters);
        }, 300);
        return () => clearTimeout(handler);
    }, [filters]);

    const fetchPurchaseReport = async () => {
        try {
            setLoading(true);
            const params = {
                startDate,
                endDate,
                tab: activeTab,
                page: currentPage,
                limit: rowsPerPage,
                ...debouncedFilters
            };

            if (!params.storeId && selectedStoreId) {
                params.storeId = selectedStoreId;
            }

            const response = await getData("reports/reports/purchase-reports", params, sessionToken);

            if (response?.status === 200 || response?.success) {
                setData(response.data?.data || []);
                setSummary(response.data?.summary || {});
                if (response.data?.pagination) {
                    setTotalItems(response.data.pagination.totalItems);
                    setTotalPages(response.data.pagination.totalPages);
                }
            } else {
                toast.error(response?.message || "Failed to fetch purchase report");
            }
        } catch (error) {
            console.error("Error fetching purchase report:", error);
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDropdownData(filters.storeId || selectedStoreId);
    }, [filters.storeId, selectedStoreId]);

    useEffect(() => {
        setCurrentPage(1);
    }, [startDate, endDate, activeTab, debouncedFilters]);

    useEffect(() => {
        fetchPurchaseReport();
    }, [startDate, endDate, activeTab, selectedStoreId, currentPage, rowsPerPage, debouncedFilters]);

    // --- Handlers ---

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleExport = () => {

        if (!data.length) {
            return toast.info("No data to export");
        }

        let exportData = [];

        switch (activeTab) {

            case "overall":

                exportData = data.map((item) => ({
                    "Invoice No": item.invoiceNo || "",
                    "Vendor Name": item.vendorName || "",
                    "Branch": item.branch || "",
                    "Products": item.productCount || 0,
                    "Total Quantity": item.totalQuantity || 0,
                    "Total Value": item.grandTotal || 0,
                    "Status": item.status || ""
                }));

                break;

            case "vendor-wise":

                exportData = data.map((item) => ({
                    "Vendor Name": item.vendorName || "",
                    "Total POs": item.poCount || 0,
                    "Total Purchase Value": item.totalPurchaseValue || 0,
                    "Total Paid": item.totalPaid || 0,
                    "Total Due": item.totalDue || 0,
                    "Last Purchase Date": item.lastPurchaseDate
                        ? new Date(item.lastPurchaseDate).toLocaleDateString("en-GB")
                        : ""
                }));

                break;

            case "category-wise":

                exportData = data.map((item) => ({
                    "Category Name": item.categoryName || "",
                    "Total Quantity": item.totalQuantity || 0,
                    "Base Value": item.totalPurchaseValue || 0,
                    "Total Tax": item.totalTax || 0,
                    "Total Value": item.totalValue || 0
                }));

                break;

            case "sku-wise":

                exportData = data.map((item) => ({
                    "Product Name": item.productName || "",
                    "SKU": item.sku || "",
                    "Total Quantity": item.totalQuantity || 0,
                    "Base Value": item.totalPurchaseValue || 0,
                    "Total Tax": item.totalTax || 0,
                    "Total Value": item.totalValue || 0
                }));

                break;

            default:
                exportData = data;
        }

        const fileName =
            `Purchase_Report_${activeTab}_${startDate}_to_${endDate}`;

        exportDataToExcel(
            exportData,
            "Purchase Report",
            `${fileName}.xlsx`
        );
    };

    const handleViewPurchase = (row) => {
        setViewPurchaseData(row);
        setViewModalOpen(true);
    };

    const resetFilters = () => {
        const resetObj = {
            supplierId: "",
            invoiceNo: "",
            storeId: selectedStoreId || "",
            categoryId: "",
            productId: ""
        };

        setStartDate(today);
        setEndDate(today);
        setDateRange("Today");
        setFilters(resetObj);
        setCurrentPage(1);
        setTableSearch("");
    };


    const isSuperAdmin = role === "SuperAdmin" || role === "SelfAdmin";
    const filteredStoreList = isSuperAdmin ? storeList : storeList.filter(s => adminStoreIds.includes(s._id));

    // --- Custom Styles for Select ---
    const customSelectStyles = {
        control: (base) => ({
            ...base,
            height: '44px',
            borderRadius: '12px',
            borderColor: '#e2e8f0',
            background: '#f8fafc',
            fontSize: '14px'
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    return (
        <div className="product-layout">
            <div className="purchase-report-container">
                <div className="purchase-report-standalone-header">
                    <div className="header-left">
                        <h1 className="page-title">Purchase Report</h1>
                        <p className="page-subtitle">Track and analyze all stock purchase transactions</p>
                    </div>
                    <div className="header-right">
                        <button className="export-dropdown-btn" onClick={handleExport}>
                            <Icon icon="mdi:export-variant" width="18" /> Export <Icon icon="mdi:chevron-down" />
                        </button>
                    </div>
                </div>

                <div className="purchase-report-tabs-wrapper">
                    <div className="purchase-report-tabs">
                        {["overall", "vendor-wise", "category-wise", "sku-wise"].map(tab => (
                            <button
                                key={tab}
                                className={`purchase-report-tab ${activeTab === tab ? "active" : ""}`}
                                onClick={() => setActiveTab(tab)}
                            >
                                {tab === "overall" ? "Overall Purchases" :
                                    tab === "vendor-wise" ? "Vendor Wise" :
                                        tab === "category-wise" ? "Category Wise" : "SKU Wise"}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="purchase-filter-bar">
                    <div className="filter-grid">
                        <div className={`filter-item ${dateRange === "Custom" ? "date-range-custom-active" : ""}`}>
                            <label>Date Range</label>
                            <div className="date-range-picker-combined">
                                <select
                                    className="filter-select date-range-select"
                                    value={dateRange}
                                    onChange={e => {
                                        const range = e.target.value;
                                        setDateRange(range);
                                        const { startDate: s, endDate: eD } = getDateRange(range, startDate, endDate);
                                        setStartDate(s);
                                        setEndDate(eD);
                                    }}
                                >
                                    <option value="Today">Today</option>
                                    <option value="Yesterday">Yesterday</option>
                                    <option value="Last 7 Days">Last 7 Days</option>
                                    <option value="Last Month">Last Month</option>
                                    <option value="Current Month">Current Month</option>
                                    <option value="Current FY">Current Financial Year</option>
                                    <option value="Previous FY">Previous Financial Year</option>
                                    <option value="Custom">Custom Date</option>
                                </select>
                                {dateRange === "Custom" ? (
                                    <div className="custom-date-inputs">
                                        <input
                                            type="date"
                                            className="filter-input date-input"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                        />
                                        <span className="date-separator">to</span>
                                        <input
                                            type="date"
                                            className="filter-input date-input"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                        />
                                    </div>
                                ) : (
                                    <div className="date-display-box">
                                        <Icon icon="mdi:calendar-outline" width="18" />
                                        <span>{startDate === endDate ? startDate : `${startDate} - ${endDate}`}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        {(activeTab === "overall" || activeTab === "vendor-wise") && (
                            <div className="filter-item">
                                <label>Vendor</label>
                                <Select
                                    options={[{ value: "", label: "All Vendors" }, ...(supplierList || []).map(s => ({ value: s._id, label: s.supplierName }))]}
                                    value={filters.supplierId ? { value: filters.supplierId, label: supplierList.find(s => s._id === filters.supplierId)?.supplierName } : null}
                                    onChange={opt => setFilters(prev => ({ ...prev, supplierId: opt ? opt.value : "" }))}
                                    styles={customSelectStyles}
                                    placeholder="Search Vendor..."
                                    isClearable
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        )}

                        {(isSuperAdmin || filteredStoreList.length > 1) && (
                            <div className="filter-item">
                                <label>Branch</label>
                                <select name="storeId" value={filters.storeId} onChange={handleFilterChange} className="filter-select">
                                    <option value="">All Branches</option>
                                    {filteredStoreList.map(s => <option key={s._id} value={s._id}>{s.branchName || s.storeName}</option>)}
                                </select>
                            </div>
                        )}

                        {(activeTab === "overall" || activeTab === "category-wise") && (
                            <div className="filter-item">
                                <label>Category</label>
                                <Select
                                    options={[{ value: "", label: "All Categories" }, ...(categoryList || []).map(c => ({ value: c._id, label: c.categoryName }))]}
                                    value={filters.categoryId ? { value: filters.categoryId, label: categoryList.find(c => c._id === filters.categoryId)?.categoryName } : null}
                                    onChange={opt => setFilters(prev => ({ ...prev, categoryId: opt ? opt.value : "" }))}
                                    styles={customSelectStyles}
                                    placeholder="Select Category..."
                                    isClearable
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        )}

                        {(activeTab === "overall" || activeTab === "sku-wise") && (
                            <div className="filter-item">
                                <label>Product</label>
                                <Select
                                    options={[{ value: "", label: "All Products" }, ...(productList || []).map(p => ({ value: p._id, label: p.title }))]}
                                    value={filters.productId ? { value: filters.productId, label: productList.find(p => p._id === filters.productId)?.title } : null}
                                    onChange={opt => setFilters(prev => ({ ...prev, productId: opt ? opt.value : "" }))}
                                    styles={customSelectStyles}
                                    placeholder="Select Product..."
                                    isClearable
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        )}

                        {(activeTab === "overall" || activeTab === "sku-wise") && (
                            <div className="filter-item">
                                <label>{activeTab === "sku-wise" ? "Search SKU / UPC Code" : "Search Product Name / Invoice #"}</label>
                                <input
                                    type="text"
                                    name="invoiceNo"
                                    placeholder={activeTab === "sku-wise" ? "Enter SKU..." : "Search..."}
                                    value={filters.invoiceNo}
                                    onChange={handleFilterChange}
                                    className="filter-input"
                                />
                            </div>
                        )}


                    </div>
                </div>

                <div className="purchase-summary-grid">
                    {[
                        { label: "Total POs Issued", value: summary.totalPOs, icon: "mdi:file-document-outline", class: "card-po" },
                        { label: "Total Purchase Value", value: (summary.totalPurchaseValue), icon: "mdi:currency-inr", class: "card-value" },
                        { label: "Total Value Received", value: (summary.totalValueReceived), icon: "mdi:truck-check-outline", class: "card-received" },
                        { label: "Total Outstanding", value: (summary.totalOutstanding), icon: "mdi:cash-clock", class: "card-due" },
                        { label: "Average Order Value", value: (summary.avgOrderValue), icon: "mdi:chart-line", class: "card-avg" }
                    ].map((card, idx) => (
                        <div className={`purchase-summary-card ${card.class}`} key={idx}>
                            <div className="purchase-card-icon"><Icon icon={card.icon} /></div>
                            <div className="purchase-card-info">
                                <span className="purchase-card-label">{card.label}</span>
                                <span className="purchase-card-value">{card.value}</span>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="purchase-table-container">
                    <div className="table-header-row">
                        <h3 className="table-title">Purchase Details</h3>
                        <div className="table-controls">
                            <div className="table-search-box">
                                <input type="text" placeholder="Search in table..." value={tableSearch} onChange={e => setTableSearch(e.target.value)} />
                                <Icon icon="mdi:magnify" />
                            </div>
                        </div>
                    </div>

                    <div className="purchase-table-wrapper" style={{ overflowX: 'auto' }}>
                        <table className="purchase-data-table">
                            <thead>
                                {activeTab === "overall" ? (
                                    <tr>
                                        <th>PO/Invoice #</th>
                                        <th>Vendor Name</th>
                                        <th>Branch</th>
                                        <th>Products</th>
                                        <th>Total Qty</th>
                                        <th>Total Value</th>
                                        <th>Status</th>
                                        <th>Action</th>
                                    </tr>
                                ) : activeTab === "vendor-wise" ? (
                                    <tr>
                                        <th>Vendor Name</th>
                                        <th>Total POs</th>
                                        <th>Total Value</th>
                                        <th>Total Paid</th>
                                        <th>Total Due</th>
                                        <th>Last Purchase</th>
                                        <th>Action</th>
                                    </tr>
                                ) : activeTab === "category-wise" ? (
                                    <tr>
                                        <th>Category Name</th>
                                        <th>Total Qty</th>
                                        <th>Base Value</th>
                                        <th>Total Tax</th>
                                        <th>Total Value</th>
                                        <th>Action</th>
                                    </tr>
                                ) : (
                                    <tr>
                                        <th>Product Name</th>
                                        <th>SKU</th>
                                        <th>Total Qty</th>
                                        <th>Base Value</th>
                                        <th>Total Tax</th>
                                        <th>Total Value</th>
                                        <th>Action</th>
                                    </tr>
                                )}
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="11" className="loading-state"><Icon icon="mdi:loading" className="spin" /> Loading...</td></tr>
                                ) : data.length === 0 ? (
                                    <tr><td colSpan="11" className="empty-state">No records found</td></tr>
                                ) : data.filter(item => {
                                    if (!tableSearch) return true;
                                    const s = tableSearch.toLowerCase();
                                    return (item.vendorName || "").toLowerCase().includes(s) ||
                                        (item.invoiceNo || "").toLowerCase().includes(s) ||
                                        (item.productName || "").toLowerCase().includes(s) ||
                                        (item.productList?.some(p => p.productName?.toLowerCase().includes(s)));
                                }).map((row, idx) => (
                                    <tr key={idx}>
                                        {activeTab === "overall" ? (
                                            <>
                                                <td>{row.invoiceNo}</td>
                                                <td>{row.vendorName}</td>
                                                <td>{row.branch}</td>
                                                <td className="product-cell">
                                                    <div className="product-count-badge">
                                                        {row.productCount} {row.productCount === 1 ? 'Product' : 'Products'}
                                                        <div className="product-tooltip">
                                                            <h6>Items in this Purchase:</h6>
                                                            <ul>
                                                                {row.productList?.map((p, i) => (
                                                                    <li key={i}>
                                                                        <span className="p-name">{p.productName}</span>
                                                                        <span className="p-cat">{p.categoryName}</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{row.totalQuantity}</td>
                                                <td>{(row.grandTotal)}</td>
                                                <td><span className={`status-badge status-${row.status?.toLowerCase()}`}>{row.status}</span></td>
                                                <td><button className="action-view-btn" onClick={() => handleViewPurchase(row)}><Icon icon="mdi:eye-outline" /></button></td>
                                            </>
                                        ) : activeTab === "vendor-wise" ? (
                                            <>
                                                <td>{row.vendorName}</td>
                                                <td>{row.poCount}</td>
                                                <td>{(row.totalPurchaseValue)}</td>
                                                <td>{(row.totalPaid)}</td>
                                                <td>{(row.totalDue)}</td>
                                                <td>{new Date(row.lastPurchaseDate).toLocaleDateString()}</td>
                                                <td><button className="action-view-btn" onClick={() => handleViewPurchase(row)}><Icon icon="mdi:eye-outline" /></button></td>
                                            </>
                                        ) : activeTab === "category-wise" ? (
                                            <>
                                                <td>{row.categoryName}</td>
                                                <td>{row.totalQuantity}</td>
                                                <td>{(row.totalPurchaseValue)}</td>
                                                <td>{(row.totalTax)}</td>
                                                <td>{(row.totalValue)}</td>
                                                <td><button className="action-view-btn" onClick={() => handleViewPurchase(row)}><Icon icon="mdi:eye-outline" /></button></td>
                                            </>
                                        ) : (
                                            <>
                                                <td>{row.productName}</td>
                                                <td>{row.sku}</td>
                                                <td>{row.totalQuantity}</td>
                                                <td>{(row.totalPurchaseValue)}</td>
                                                <td>{(row.totalTax)}</td>
                                                <td>{(row.totalValue)}</td>
                                                <td><button className="action-view-btn" onClick={() => handleViewPurchase(row)}><Icon icon="mdi:eye-outline" /></button></td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="table-pagination-row">
                        <div className="pagination-info">Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, totalItems)} of {totalItems} entries</div>
                        <div className="pagination-controls">
                            <button className="pagination-arrow" disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)}><Icon icon="mdi:chevron-left" /></button>
                            <div className="pagination-number active">{currentPage}</div>
                            <button className="pagination-arrow" disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)}><Icon icon="mdi:chevron-right" /></button>
                        </div>
                        <div className="rows-per-page">
                            <span>Rows per page:</span>
                            <select value={rowsPerPage} onChange={e => setRowsPerPage(Number(e.target.value))}>
                                {[10, 20, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </div>
                    </div>
                </div>

                {viewModalOpen && (
                    <div className="view-modal-overlay" onClick={() => setViewModalOpen(false)}>
                        <div className="view-modal-content" onClick={e => e.stopPropagation()}>
                            <div className="view-modal-header">
                                <h3>Purchase Details</h3>
                                <button className="close-btn" onClick={() => setViewModalOpen(false)}><Icon icon="mdi:close" /></button>
                            </div>
                            <div className="view-modal-body">
                                <div className="detail-grid">
                                    {activeTab === "overall" ? (
                                        <div className="overall-view-container">
                                            <div className="detail-grid">
                                                <div className="detail-item"><label>Invoice No:</label> <span>{viewPurchaseData?.invoiceNo}</span></div>
                                                <div className="detail-item"><label>Vendor:</label> <span>{viewPurchaseData?.vendorName}</span></div>
                                                <div className="detail-item"><label>Branch:</label> <span>{viewPurchaseData?.branch}</span></div>
                                                <div className="detail-item"><label>Date:</label> <span>{new Date(viewPurchaseData?.purchaseDate).toLocaleDateString()}</span></div>
                                                <div className="detail-item"><label>Total Amount:</label> <span>{(viewPurchaseData?.grandTotal)}</span></div>
                                                <div className="detail-item"><label>Status:</label> <span className={`status-badge status-${viewPurchaseData?.status?.toLowerCase()}`}>{viewPurchaseData?.status}</span></div>
                                            </div>

                                            <div className="modal-section-title">Purchased Items</div>
                                            <div className="modal-table-wrapper">
                                                <table className="modal-data-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Product</th>
                                                            <th>Category</th>
                                                            <th>Qty</th>
                                                            <th>Cost</th>
                                                            <th>Tax</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {viewPurchaseData?.productList?.map((p, i) => (
                                                            <tr key={i}>
                                                                <td>{p.productName}</td>
                                                                <td>{p.categoryName}</td>
                                                                <td>{p.qty}</td>
                                                                <td>{(p.price)}</td>
                                                                <td>{(p.gst)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="detail-grid">
                                            {activeTab === "vendor-wise" && (
                                                <>
                                                    <div className="detail-item"><label>Vendor:</label> <span>{viewPurchaseData?.vendorName}</span></div>
                                                    <div className="detail-item"><label>PO Count:</label> <span>{viewPurchaseData?.poCount}</span></div>
                                                    <div className="detail-item"><label>Total Value:</label> <span>{(viewPurchaseData?.totalPurchaseValue)}</span></div>
                                                    <div className="detail-item"><label>Total Paid:</label> <span>{(viewPurchaseData?.totalPaid)}</span></div>
                                                    <div className="detail-item"><label>Total Due:</label> <span>{(viewPurchaseData?.totalDue)}</span></div>
                                                </>
                                            )}
                                            {activeTab === "category-wise" && (
                                                <>
                                                    <div className="detail-item"><label>Category:</label> <span>{viewPurchaseData?.categoryName}</span></div>
                                                    <div className="detail-item"><label>Total Qty:</label> <span>{viewPurchaseData?.totalQuantity}</span></div>
                                                    <div className="detail-item"><label>Base Value:</label> <span>{(viewPurchaseData?.totalPurchaseValue)}</span></div>
                                                    <div className="detail-item"><label>Total Tax:</label> <span>{(viewPurchaseData?.totalTax)}</span></div>
                                                    <div className="detail-item"><label>Total Value:</label> <span>{(viewPurchaseData?.totalValue)}</span></div>
                                                </>
                                            )}
                                            {activeTab === "sku-wise" && (
                                                <>
                                                    <div className="detail-item"><label>Product:</label> <span>{viewPurchaseData?.productName}</span></div>
                                                    <div className="detail-item"><label>SKU:</label> <span>{viewPurchaseData?.sku}</span></div>
                                                    <div className="detail-item"><label>Total Qty:</label> <span>{viewPurchaseData?.totalQuantity}</span></div>
                                                    <div className="detail-item"><label>Total Value:</label> <span>{(viewPurchaseData?.totalValue)}</span></div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>

    );
};

export default PurchaseReport;
